import type { ReactNode } from "react";
import {
  reportPhotoLimits,
  type ReportAnswer,
  type ReportQuestion,
} from "@myfm/domain";
import type { PhotoTransferStage } from "../report-photo-transfer";

export type RetainedReportPhoto = { id: string; question_id: string };
export function ReportEvidenceGallery({
  question,
  answer,
  photos,
  readOnly,
  busy,
  renderPhoto,
  onChange,
  onPick,
}: {
  question: ReportQuestion;
  answer: ReportAnswer;
  photos: RetainedReportPhoto[];
  readOnly: boolean;
  busy: boolean;
  renderPhoto: (id: string) => ReactNode;
  onChange: (patch: Partial<ReportAnswer>) => void;
  onPick: (file: File) => void;
}) {
  const attached = answer.photoIds ?? [],
    retained = photos.filter((p) => p.question_id === question.id);
  const ids = [
    ...new Set([...attached, ...(readOnly ? [] : retained.map((p) => p.id))]),
  ];
  const exceptionAllowed =
    question.type === "photo" ||
    (question.type === "check" && answer.value === "Fail");
  return (
    <div
      className="report-evidence-gallery"
      aria-label={question.label + " photographs"}
    >
      <p>
        {attached.length} attached · {retained.length} retained for this
        question
      </p>
      <div className="report-photos">
        {ids.map((id) => (
          <figure key={id}>
            {renderPhoto(id)}
            <figcaption>
              {question.label} ·{" "}
              {attached.includes(id)
                ? "Attached to answer"
                : "Retained, not attached"}
            </figcaption>
            {!readOnly &&
              (attached.includes(id) ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    onChange({
                      photoIds: attached.filter((value) => value !== id),
                    })
                  }
                >
                  Remove from answer
                </button>
              ) : (
                <button
                  type="button"
                  disabled={
                    busy ||
                    attached.length >= reportPhotoLimits.attachedPerQuestion
                  }
                  onClick={() => onChange({ photoIds: [...attached, id] })}
                >
                  Attach retained photograph
                </button>
              ))}
          </figure>
        ))}
      </div>
      {!readOnly && (
        <label>
          Add photograph
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            disabled={
              busy || attached.length >= reportPhotoLimits.attachedPerQuestion
            }
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onPick(file);
            }}
          />
        </label>
      )}
      {answer.photoException &&
        (readOnly ? (
          <p className="report-exception">
            Photo safety/access exception: {answer.photoException.reason}
          </p>
        ) : (
          <>
            <label>
              Safety or access restriction (10–4000 characters)
              <textarea
                minLength={10}
                maxLength={4000}
                disabled={busy}
                value={answer.photoException.reason}
                onChange={(e) =>
                  onChange({ photoException: { reason: e.target.value } })
                }
              />
            </label>
            <p>
              Describe why a photograph cannot safely be taken or the area
              cannot be accessed. It remains an issue for review. Connection and
              upload problems need retry.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => onChange({ photoException: undefined })}
            >
              Remove photo exception
            </button>
          </>
        ))}
      {!readOnly &&
        exceptionAllowed &&
        !attached.length &&
        !answer.photoException && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onChange({ photoException: { reason: "" } })}
          >
            Cannot take this photo safely or access the area
          </button>
        )}
    </div>
  );
}
const stageText: Record<PhotoTransferStage, string> = {
  "saving-draft": "Saving the existing draft before uploading.",
  uploading:
    "Uploading and checking the photograph. It is not yet confirmed as retained.",
  attaching:
    "Photograph retained. Attaching it to the original answer is not yet confirmed.",
  reloading: "Attachment saved. Reloading the retained report.",
  complete: "Photograph and answer saved to BG.",
};
export function ReportPhotoRecovery({
  question,
  fileName,
  stage,
  busy,
  onRetry,
}: {
  question: string;
  fileName: string;
  stage: PhotoTransferStage;
  busy: boolean;
  onRetry: () => void;
}) {
  return (
    <section className="report-photo-recovery" aria-label="Photo transfer">
      <h2>
        {question} · {fileName}
      </h2>
      <p role="status">{stageText[stage]}</p>
      {!busy && (
        <>
          <p>
            The last step did not finish. Retry keeps the same file or retained
            photo receipt. Reload can inspect saved evidence; it discards
            unsaved input.
          </p>
          <button type="button" onClick={onRetry}>
            Retry photograph
          </button>
        </>
      )}
    </section>
  );
}
